import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AssessQueryKeys, useAssessmentsApi } from '@/api';
import { AddOrganizationRequest, AddVerticalRequest, AssessmentCreate } from '@/api/api-client';
import { mapAssessments } from '@/api/api-mappers';
import { GetAssessmentsRequest } from '@/api/api-queries/duck';

export const useGetAssessments = (request: GetAssessmentsRequest) => {
  const { assessmentsApi } = useAssessmentsApi();

  const { data, isLoading } = useQuery({
    queryKey: [AssessQueryKeys.assessmentsList, request],
    queryFn: async () => {
      const response = await assessmentsApi.getAssessmentsApiAssessmentsGet(
        request.name,
        request.standard,
        request.organizationId,
        request.scoresChecked,
        request.sortBy,
        request.sortOrder,
        request.page ?? 1,
        request.pageSize,
        null, // accessToken parameter
        undefined, // options parameter
      );

      return response.data;
    },
    enabled: !!request.pageSize && request.pageSize > 0,
    retry: 1,
  });

  const assessments = data ? mapAssessments(data.data) : [];

  return {
    assessments: assessments,
    isAssessmentsLoading: isLoading,
    totalPages: data?.meta.totalPages ?? 1,
    totalItems: data?.meta.totalItems ?? 1,
  };
};

export const useCreateAssessment = () => {
  const queryClient = useQueryClient();
  const { assessmentsApi } = useAssessmentsApi();

  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: (request: AssessmentCreate) => assessmentsApi.createAssessmentApiAssessmentsPost(request),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AssessQueryKeys.assessmentsList],
      });
    },
  });

  return {
    createAssessment: mutateAsync,
    isCreating: isPending,
    isError,
    error,
  };
};

export const useUpdateAssessment = () => {
  const queryClient = useQueryClient();
  const { assessmentsApi } = useAssessmentsApi();

  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: (request: AssessmentCreate & { assessment_id: string }) => {
      const { assessment_id, ...assessmentData } = request;

      return assessmentsApi.updateAssessmentApiAssessmentsAssessmentIdPut(
        assessment_id,
        assessmentData,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AssessQueryKeys.assessmentsList],
      });
    },
  });

  return {
    updateAssessment: mutateAsync,
    isUpdating: isPending,
    isError,
    error,
  };
};


export const useDeleteAssessment = () => {
  const queryClient = useQueryClient();
  const { assessmentsApi } = useAssessmentsApi();

  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: (id: string) => assessmentsApi.deleteAssessmentApiAssessmentsAssessmentIdDelete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AssessQueryKeys.assessmentsList],
      });
    },
  });

  return {
    deleteAssessment: mutateAsync,
    isDeleting: isPending,
    isError,
    error,
  };
};

export const useAddOrgToAssessment = () => {
  const queryClient = useQueryClient();
  const { assessmentsApi } = useAssessmentsApi();

  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: (request: AddOrganizationRequest & { assessmentId: string }) => {
      const { assessmentId, ...orgRequest } = request;

      return assessmentsApi.addOrganizationToAssessmentApiAssessmentsAssessmentIdAddOrganizationPut(
        assessmentId,
        orgRequest,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AssessQueryKeys.assessmentsList],
      });
    },
  });

  return {
    addOrgToAssessment: mutateAsync,
    isPending,
    isError,
    error,
  };
};

export const useAddPopVertToAssessment = () => {
  const queryClient = useQueryClient();
  const { assessmentsApi } = useAssessmentsApi();

  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: (request: AddVerticalRequest & { assessmentId: string }) => {
      const { assessmentId, ...orgRequest } = request;

      return assessmentsApi.addPopulationVerticalToAssessmentApiAssessmentsAssessmentIdAddVerticalPost(
        assessmentId,
        orgRequest,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AssessQueryKeys.assessmentsList],
      });
    },
  });

  return {
    addPopVertToAssessment: mutateAsync,
    isPending,
    isError,
    error,
  };
};